import React, { useState, useEffect } from 'react';
import { clientsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../lib/apiError';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Plus,
    Search,
    MoreVertical,
    Pencil,
    Trash2,
    Building2,
    Phone,
    Mail,
    MapPin,
    Camera,
    Loader2,
    AlertTriangle,
    Upload,
    Map as MapIcon
} from 'lucide-react';
import { ClientMiniMap } from '../components/ClientMiniMap';
import { ClientRelazioni } from '../components/ClientRelazioni';
import { toast } from 'sonner';

const COUNTRIES = [
    'Italia', 'Spagna', 'Portogallo', 'UK', 'Irlanda', 'Francia', 'Belgio', 'Svizzera',
    'Germania', 'Polonia', 'Norvegia', 'Danimarca', 'Svezia', 'Finlandia',
    'Repubblica Ceca', 'Croazia', 'Bosnia', 'Serbia', 'Bulgaria', 'Grecia',
    'Albania', 'Kosovo', 'Olanda', 'Israele', 'Emirati Arabi', 'Libia',
    'Baltici', 'Ucraina', 'Georgia', 'Mongolia', 'USA', 'Canada', 'Altro'
];

const ClientsPage = () => {
    const { isAdmin, canViewTeam } = useAuth();
    const [clients, setClients] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [sellerFilter, setSellerFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [locationClient, setLocationClient] = useState(null);
    const [formData, setFormData] = useState({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        country: '',
        address: '',
        notes: '',
        first_contact_place: '',
        first_contact_date: '',
        role: '',
        website: '',
        linkedin: '',
        assigned_to: ''
    });
    const [scanning, setScanning] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignCountries, setAssignCountries] = useState([]);
    const [assignSeller, setAssignSeller] = useState('');
    const [assignBusy, setAssignBusy] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importSeller, setImportSeller] = useState('');
    const [importCountry, setImportCountry] = useState('all');
    const [importBusy, setImportBusy] = useState(false);
    const [importUpdateAssign, setImportUpdateAssign] = useState(false);
    const importInputRef = React.useRef(null);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        loadClients();
        if (canViewTeam()) {
            usersAPI.getSellers()
                .then((res) => setSellers(res.data || []))
                .catch(() => {});
        }
    }, []);

    const loadClients = async () => {
        try {
            const response = await clientsAPI.getAll();
            setClients(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento dei clienti');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            company_name: '',
            contact_name: '',
            email: '',
            phone: '',
            country: '',
            address: '',
            notes: '',
            first_contact_place: '',
            first_contact_date: '',
            role: '',
            website: '',
            linkedin: '',
            assigned_to: ''
        });
        setEditingClient(null);
    };

    const handleOpenDialog = (client = null) => {
        if (client) {
            setEditingClient(client);
            setFormData({
                company_name: client.company_name,
                contact_name: client.contact_name,
                email: client.email || '',
                phone: client.phone || '',
                country: client.country,
                address: client.address || '',
                notes: client.notes || '',
                first_contact_place: client.first_contact_place || '',
                first_contact_date: client.first_contact_date ? client.first_contact_date.substring(0, 10) : '',
                role: client.role || '',
                website: client.website || '',
                linkedin: client.linkedin || '',
                assigned_to: client.assigned_to || ''
            });
        } else {
            resetForm();
        }
        setIsDialogOpen(true);
    };

    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.substring(comma + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleScanCard = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
            toast.error('Formato non supportato. Usa JPEG, PNG o WEBP');
            e.target.value = '';
            return;
        }
        setScanning(true);
        try {
            const b64 = await fileToBase64(file);
            const { data } = await clientsAPI.scanCard(b64, file.type);
            const countryMatch = COUNTRIES.find(c => c.toLowerCase() === (data.country || '').toLowerCase());
            setFormData(prev => ({
                ...prev,
                company_name: data.company_name || prev.company_name,
                contact_name: data.contact_name || prev.contact_name,
                email: data.email || prev.email,
                phone: data.phone || prev.phone,
                country: countryMatch || prev.country,
                address: data.address || prev.address,
                role: data.role || prev.role,
                website: data.website || prev.website,
                linkedin: data.linkedin || prev.linkedin,
            }));
            const filled = Object.values(data).filter(v => v && v !== 'null').length;
            toast.success(`Biglietto scansionato: ${filled} campi compilati. Controlla e salva.`);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nella scansione del biglietto'));
        } finally {
            setScanning(false);
            e.target.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (payload.first_contact_date) {
                payload.first_contact_date = new Date(payload.first_contact_date).toISOString();
            } else {
                delete payload.first_contact_date;
            }
            // Strip empty optional fields so backend doesn't try to validate them (e.g. EmailStr on '')
            const optionalFields = ['email', 'phone', 'address', 'notes', 'first_contact_place', 'role', 'website', 'linkedin', 'assigned_to'];
            optionalFields.forEach((f) => {
                if (payload[f] === '' || payload[f] === null || payload[f] === undefined) {
                    delete payload[f];
                }
            });

            if (editingClient) {
                await clientsAPI.update(editingClient.id, payload);
                toast.success('Cliente aggiornato con successo');
            } else {
                await clientsAPI.create(payload);
                toast.success('Cliente creato con successo');
            }
            setIsDialogOpen(false);
            resetForm();
            loadClients();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        }
    };

    const handleAssignCountries = async () => {
        if (!assignSeller || assignCountries.length === 0) return;
        setAssignBusy(true);
        try {
            const res = await clientsAPI.assignCountries(assignCountries, assignSeller);
            const n = res.data?.reassigned_clients ?? 0;
            toast.success(`${n} clienti riassegnati · ${assignCountries.length} nazioni impostate come territorio`);
            setAssignOpen(false);
            setAssignCountries([]);
            setAssignSeller('');
            loadClients();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore durante l\'assegnazione'));
        } finally {
            setAssignBusy(false);
        }
    };

    const handleImport = async () => {
        if (!importFile || !importSeller) return;
        setImportBusy(true);
        try {
            const country = importCountry === 'all' ? null : importCountry;
            const res = await clientsAPI.importExcel(importFile, importSeller, country, importUpdateAssign);
            const d = res.data || {};
            toast.success(`Importati ${d.imported ?? 0} · aggiornati ${d.updated_assignments ?? 0} · ${d.skipped_existing ?? 0} già presenti`);
            setImportOpen(false);
            setImportFile(null);
            setImportSeller('');
            setImportCountry('all');
            setImportUpdateAssign(false);
            if (importInputRef.current) importInputRef.current.value = '';
            loadClients();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore durante l\'importazione'));
        } finally {
            setImportBusy(false);
        }
    };

    const handleDelete = async (clientId) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
        
        try {
            await clientsAPI.delete(clientId);
            toast.success('Cliente eliminato');
            loadClients();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'eliminazione'));
        }
    };

    const handleStatusChange = async (clientId, status) => {
        try {
            await clientsAPI.update(clientId, { status });
            toast.success('Stato aggiornato');
            loadClients();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nell\'aggiornamento'));
        }
    };

    const filteredClients = clients.filter(client => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            (client.company_name || '').toLowerCase().includes(term) ||
            (client.contact_name || '').toLowerCase().includes(term) ||
            (client.country || '').toLowerCase().includes(term) ||
            (client.email || '').toLowerCase().includes(term) ||
            (client.address || '').toLowerCase().includes(term) ||
            (client.notes || '').toLowerCase().includes(term);

        const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
        const matchesSeller = sellerFilter === 'all' || client.assigned_to === sellerFilter;
        const matchesCountry = countryFilter === 'all' || client.country === countryFilter;

        return matchesSearch && matchesStatus && matchesSeller && matchesCountry;
    });

    const sellerNameById = React.useMemo(
        () => Object.fromEntries(sellers.map((s) => [s.id, s.name])),
        [sellers]
    );
    const ownersByCountry = React.useMemo(() => {
        const m = {};
        sellers.forEach((s) => (s.countries || []).forEach((c) => {
            (m[c] = m[c] || []).push(s.name);
        }));
        return m;
    }, [sellers]);
    const allCountries = React.useMemo(() => {
        const set = new Set(COUNTRIES);
        clients.forEach((c) => { if (c.country) set.add(c.country); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
    }, [clients]);
    const getCodice = (client) => {
        const m = /Codice:\s*(\S+)/.exec(client.notes || '');
        return m ? m[1] : '';
    };

    const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pagedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter, sellerFilter, countryFilter]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge className="status-active">Attivo</Badge>;
            case 'inactive':
                return <Badge className="status-inactive">Inattivo</Badge>;
            case 'lost':
                return <Badge className="status-lost">Perso</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="clients-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Clienti</h1>
                    <p className="text-muted-foreground">
                        {clients.length} clienti totali
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                {isAdmin() && (
                    <Button variant="outline" onClick={() => { setAssignCountries([]); setAssignSeller(''); setAssignOpen(true); }} data-testid="assign-countries-btn">
                        <MapPin size={18} className="mr-2" /> Assegna nazioni
                    </Button>
                )}
                {isAdmin() && (
                    <Button variant="outline" onClick={() => { setImportFile(null); setImportSeller(''); setImportCountry('all'); setImportOpen(true); }} data-testid="import-clients-btn">
                        <Upload size={18} className="mr-2" /> Importa Excel
                    </Button>
                )}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button 
                            className="btn-primary font-heading"
                            onClick={() => handleOpenDialog()}
                            data-testid="add-client-btn"
                        >
                            <Plus size={20} className="mr-2" />
                            NUOVO CLIENTE
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">
                                {editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            {/* Business Card Scan */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                capture="environment"
                                onChange={handleScanCard}
                                className="hidden"
                                data-testid="card-scan-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 border-dashed border-primary/50 text-primary hover:bg-primary/5"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={scanning}
                                data-testid="card-scan-btn"
                            >
                                {scanning ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Scansione in corso...
                                    </>
                                ) : (
                                    <>
                                        <Camera size={18} className="mr-2" />
                                        SCANSIONA BIGLIETTO DA VISITA
                                    </>
                                )}
                            </Button>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                                <span className="relative flex justify-center text-xs uppercase tracking-wider bg-background px-3 text-muted-foreground">oppure compila manualmente</span>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Azienda *</Label>
                                    <Input
                                        value={formData.company_name}
                                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                                        required
                                        data-testid="client-company"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Referente *</Label>
                                    <Input
                                        value={formData.contact_name}
                                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                                        required
                                        data-testid="client-contact"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Ruolo / Titolo</Label>
                                    <Input
                                        value={formData.role}
                                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                                        placeholder="Es. Responsabile Acquisti, CEO"
                                        data-testid="client-role"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        data-testid="client-email"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Telefono</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        data-testid="client-phone"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Paese *</Label>
                                    <Select
                                        value={formData.country}
                                        onValueChange={(value) => setFormData({...formData, country: value})}
                                    >
                                        <SelectTrigger data-testid="client-country">
                                            <SelectValue placeholder="Seleziona paese" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {allCountries.map((country) => (
                                                <SelectItem key={country} value={country}>
                                                    {country}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {isAdmin() && (
                                    <div className="col-span-2">
                                        <Label className="text-xs uppercase tracking-wider">Commerciale di riferimento</Label>
                                        <Select
                                            value={formData.assigned_to || ''}
                                            onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                                        >
                                            <SelectTrigger data-testid="client-assigned-to">
                                                <SelectValue placeholder="Seleziona commerciale" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {sellers.map((s) => (
                                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Indirizzo</Label>
                                    <Input
                                        value={formData.address}
                                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                                        data-testid="client-address"
                                    />
                                </div>
                                {editingClient && (
                                    <div className="col-span-2">
                                        <ClientMiniMap client={editingClient} height={180} />
                                    </div>
                                )}
                                {editingClient && (
                                    <div className="col-span-2 border-t pt-3">
                                        <ClientRelazioni client={editingClient} />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Sito Web</Label>
                                    <Input
                                        value={formData.website}
                                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                                        placeholder="https://..."
                                        data-testid="client-website"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">LinkedIn</Label>
                                    <Input
                                        value={formData.linkedin}
                                        onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                                        placeholder="linkedin.com/in/..."
                                        data-testid="client-linkedin"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Luogo Primo Contatto</Label>
                                    <Input
                                        value={formData.first_contact_place}
                                        onChange={(e) => setFormData({...formData, first_contact_place: e.target.value})}
                                        placeholder="Es. Fiera di Verona"
                                        data-testid="client-first-contact-place"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Data Primo Contatto</Label>
                                    <Input
                                        type="date"
                                        value={formData.first_contact_date}
                                        onChange={(e) => setFormData({...formData, first_contact_date: e.target.value})}
                                        data-testid="client-first-contact-date"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase tracking-wider">Note</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        rows={3}
                                        data-testid="client-notes"
                                    />
                                </div>
                            </div>
                            <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t flex justify-end gap-3 z-10">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    Annulla
                                </Button>
                                <Button type="submit" className="btn-primary" data-testid="client-submit">
                                    {editingClient ? 'AGGIORNA' : 'CREA'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                {isAdmin() && (
                <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="assign-countries-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">Assegna nazioni a un commerciale</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Commerciale</Label>
                                <Select value={assignSeller} onValueChange={setAssignSeller}>
                                    <SelectTrigger data-testid="assign-seller"><SelectValue placeholder="Seleziona commerciale" /></SelectTrigger>
                                    <SelectContent>
                                        {sellers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Nazioni</Label>
                                <p className="text-xs text-muted-foreground mb-2">Verranno riassegnati tutti i clienti delle nazioni scelte e impostate come territorio del commerciale (i clienti futuri di quelle nazioni andranno automaticamente a lui).</p>
                                <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto border rounded-sm p-2">
                                    {allCountries.map((c) => {
                                        const checked = assignCountries.includes(c);
                                        return (
                                            <label key={c} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-muted/50 cursor-pointer">
                                                <input type="checkbox" checked={checked} onChange={() => setAssignCountries((prev) => checked ? prev.filter((x) => x !== c) : [...prev, c])} data-testid={`assign-country-${c}`} />
                                                {c}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            <Button className="btn-primary w-full font-heading" disabled={assignBusy || !assignSeller || assignCountries.length === 0} onClick={handleAssignCountries} data-testid="assign-countries-submit">
                                {assignBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin size={18} className="mr-2" />}
                                Assegna {assignCountries.length > 0 ? `(${assignCountries.length})` : ''}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                )}
                {isAdmin() && (
                <Dialog open={importOpen} onOpenChange={setImportOpen}>
                    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="import-clients-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">Importa clienti da Excel</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <p className="text-xs text-muted-foreground">
                                Carica il file di export (.xlsx). Verranno importati solo i clienti non ancora presenti (in base a Codice o Ragione Sociale). I duplicati vengono saltati automaticamente.
                            </p>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">File Excel</Label>
                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                    className="block w-full text-sm mt-1 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
                                    data-testid="import-file-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Assegna al commerciale</Label>
                                <Select value={importSeller} onValueChange={setImportSeller}>
                                    <SelectTrigger data-testid="import-seller"><SelectValue placeholder="Seleziona commerciale" /></SelectTrigger>
                                    <SelectContent>
                                        {sellers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Filtra per nazione (opzionale)</Label>
                                <Select value={importCountry} onValueChange={setImportCountry}>
                                    <SelectTrigger data-testid="import-country"><SelectValue placeholder="Tutte le nazioni" /></SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        <SelectItem value="all">Tutte le nazioni</SelectItem>
                                        <SelectItem value="Romania">Romania</SelectItem>
                                        {allCountries.filter((c) => c !== 'Romania').map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">Importa solo i clienti della nazione scelta. Lascia "Tutte" per importare l'intero file.</p>
                            </div>
                            <label className="flex items-start gap-3 rounded-sm border p-3 cursor-pointer hover:bg-muted/40" data-testid="import-update-assign-label">
                                <Checkbox checked={importUpdateAssign} onCheckedChange={(v) => setImportUpdateAssign(!!v)} data-testid="import-update-assign" />
                                <span className="text-sm">
                                    <span className="font-medium">Aggiorna il commerciale dei clienti già presenti</span>
                                    <span className="block text-xs text-muted-foreground">Imposta il "commerciale di riferimento" in base alla colonna Agente del file (anche per i clienti già in anagrafica).</span>
                                </span>
                            </label>
                            <Button className="btn-primary w-full font-heading" disabled={importBusy || !importFile || !importSeller} onClick={handleImport} data-testid="import-clients-submit">
                                {importBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload size={18} className="mr-2" />}
                                Importa clienti
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                )}
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Cerca per azienda, codice, referente, paese, email, città o P.IVA..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="client-search"
                            />
                        </div>
                        {canViewTeam() && (
                            <Select value={sellerFilter} onValueChange={setSellerFilter}>
                                <SelectTrigger className="w-full sm:w-[220px]" data-testid="client-seller-filter">
                                    <SelectValue placeholder="Commerciale" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i commerciali</SelectItem>
                                    {sellers.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Select value={countryFilter} onValueChange={setCountryFilter}>
                            <SelectTrigger className="w-full sm:w-[180px]" data-testid="client-country-filter">
                                <SelectValue placeholder="Nazione" />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                <SelectItem value="all">Tutte le nazioni</SelectItem>
                                {allCountries.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]" data-testid="client-status-filter">
                                <SelectValue placeholder="Stato" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="active">Attivi</SelectItem>
                                <SelectItem value="inactive">Inattivi</SelectItem>
                                <SelectItem value="lost">Persi</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Clients Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full data-table table-zebra">
                            <thead>
                                <tr className="border-b bg-muted/30">
                                    <th className="text-left py-4 px-4">Azienda</th>
                                    <th className="text-left py-4 px-4">Codice</th>
                                    <th className="text-left py-4 px-4">Referente</th>
                                    <th className="text-left py-4 px-4">Contatti</th>
                                    <th className="text-left py-4 px-4">Paese</th>
                                    {canViewTeam() && <th className="text-left py-4 px-4">Commerciale</th>}
                                    <th className="text-left py-4 px-4">Stato</th>
                                    <th className="text-right py-4 px-4">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.length === 0 ? (
                                    <tr>
                                        <td colSpan={canViewTeam() ? 8 : 7} className="text-center py-8 text-muted-foreground">
                                            Nessun cliente trovato
                                        </td>
                                    </tr>
                                ) : (
                                    pagedClients.map((client) => (
                                        <tr key={client.id} className="border-b hover:bg-muted/30" data-testid={`client-row-${client.id}`}>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center text-white">
                                                        <Building2 size={18} />
                                                    </div>
                                                    <span className="font-medium">{client.company_name}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                {getCodice(client) ? (
                                                    <span className="font-mono text-sm text-muted-foreground" data-testid={`client-codice-${client.id}`}>{getCodice(client)}</span>
                                                ) : (
                                                    <span className="text-muted-foreground/40">—</span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4">{client.contact_name}</td>
                                            <td className="py-4 px-4">
                                                <div className="space-y-1">
                                                    {client.email && (
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <Mail size={14} />
                                                            <span>{client.email}</span>
                                                        </div>
                                                    )}
                                                    {client.phone && (
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <Phone size={14} />
                                                            <span>{client.phone}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-2 hover:text-primary transition-colors"
                                                    onClick={() => setLocationClient(client)}
                                                    title="Mostra sulla mappa"
                                                    data-testid={`client-country-btn-${client.id}`}
                                                >
                                                    <MapPin size={14} className={client.geo_precision && client.geo_precision !== 'none' ? 'text-emerald-600' : 'text-muted-foreground'} />
                                                    {client.country}
                                                </button>
                                            </td>
                                            {canViewTeam() && (
                                                <td className="py-4 px-4" data-testid={`client-country-owner-${client.id}`}>
                                                    {ownersByCountry[client.country]?.length ? (
                                                        <span className="text-sm">{ownersByCountry[client.country].join(', ')}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground/40">—</span>
                                                    )}
                                                </td>
                                            )}
                                            <td className="py-4 px-4">
                                                {getStatusBadge(client.status)}
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" data-testid={`client-actions-${client.id}`}>
                                                            <MoreVertical size={18} />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleOpenDialog(client)}>
                                                            <Pencil size={16} className="mr-2" />
                                                            Modifica
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => setLocationClient(client)} data-testid={`client-location-${client.id}`}>
                                                            <MapIcon size={16} className="mr-2" />
                                                            Posizione sulla mappa
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'active')}>
                                                            Segna come Attivo
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'inactive')}>
                                                            Segna come Inattivo
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleStatusChange(client.id, 'lost')}>
                                                            Segna come Perso
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleDelete(client.id)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 size={16} className="mr-2" />
                                                            Elimina
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Posizione cliente */}
            <Dialog open={!!locationClient} onOpenChange={(v) => { if (!v) setLocationClient(null); }}>
                <DialogContent className="max-w-xl" data-testid="client-location-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">{locationClient?.company_name}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground -mt-2">
                        {[locationClient?.address, locationClient?.country].filter(Boolean).join(' · ') || 'Indirizzo non indicato'}
                    </p>
                    <ClientMiniMap client={locationClient} height={300} />
                </DialogContent>
            </Dialog>

            {/* Pagination */}
            {filteredClients.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="clients-pagination">
                    <p className="text-sm text-muted-foreground">
                        {(currentPage - 1) * PAGE_SIZE + 1}
                        {' - '}
                        {Math.min(currentPage * PAGE_SIZE, filteredClients.length)}
                        {' di '}
                        {filteredClients.length}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage <= 1}
                            onClick={() => setPage(currentPage - 1)}
                            data-testid="clients-prev-page"
                        >
                            Precedente
                        </Button>
                        <span className="text-sm px-2" data-testid="clients-page-indicator">
                            Pagina {currentPage} / {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage >= totalPages}
                            onClick={() => setPage(currentPage + 1)}
                            data-testid="clients-next-page"
                        >
                            Successiva
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientsPage;
